
import {
  ClearRounded,
  EditRounded,
  HorizontalSplitRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  Button as MuiButton,
} from "@mui/material";

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Alert, Dialog, Table } from "../../layout";
import * as actions from "../../api/actions/pos";
import { controller, endpoints } from "../../api/actions/api";
import { AppContext } from "../../App";
import { DropLi } from "../../hooks";
import BarcodeGen from "./BarcodeGen";
import { Button } from "../../controls";

import ReportPrintButton from "../reports/ReportPrintButton";
import { formatCurrency } from "../../hooks/Currency";

//------------------------------------------------
// Reusable Product Details
//------------------------------------------------
const ProductDetailsContent = ({ product, store, t }) => {
  const currency = (value) =>
    value === null || value === undefined || value === ""
      ? "-"
      : formatCurrency(Number(value), store);

  const fields = [
    { label: t("name"), value: product.name },
    { label: t("barcode"), value: product.barcode },
    { label: t("category"), value: product.category },
    { label: t("subcategory"), value: product.subCat },
    { label: t("unitType"), value: product.unit },
    { label: t("tax"), value: product.tax },
    {
      label: t("purchasePrice"),
      value: currency(product.purchPrice),
    },
    {
      label: t("profitMargin"),
      value: product.profitMargin,
    },
    {
      label: t("sellPrice"),
      value: currency(product.sellPrice),
    },
    { label: t("alertQty"), value: product.alertQty },
    { label: t("image"), value: product.imageStatus },
  ];

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          mb: 3,
        }}
      >
        {t("productDetails", {
          defaultValue: "Product Details",
        })}
      </Typography>

      {fields.map((field, index) => (
        <React.Fragment key={index}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              py: 1,
            }}
          >
            <Typography
              sx={{ fontWeight: 600, minWidth: "35%" }}
            >
              {field.label}:
            </Typography>

            <Typography
              sx={{
                textAlign: "right",
                overflowWrap: "anywhere",
              }}
            >
              {field.value === null ||
              field.value === undefined ||
              field.value === ""
                ? "-"
                : field.value}
            </Typography>
          </Box>
          <Divider />
        </React.Fragment>
      ))}
    </Box>
  );
};

const Products = (props) => {
  const { t } = useTranslation();

  const { _getById, _productsList, _delete, _store } = props;
  const { UnitType, BoolType } = DropLi();

  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);
  const detailsRef = useRef(null);

  const [barCode, setBarCode] = useState("abcd");
  const [showBarcode, setShowBarcode] = useState(false);

  const [showView, setShowView] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Delete Product
  //------------------------------------------------
  const handleDlt = (id) => {
    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("areYouSure"),
      subTitle: t("deleteProductConfirmation"),
      onConfirm: () => {
        const onComplete = () => {
          setAlert({
            isOpen: true,
            type: "success",
            title: t("successful"),
            subTitle: t("successfullyDeleted"),
          });
        };

        _delete(
          endpoints.Product,
          id,
          actions.ActionType.RmvProduct,
          onComplete
        );
      },
    });
  };

  //------------------------------------------------
  // Barcode
  //------------------------------------------------
  const handleBarcode = (barcode) => {
    setBarCode(barcode);
    setShowBarcode(true);
  };

  //------------------------------------------------
  // Load Products
  //------------------------------------------------
  useEffect(() => {
    if (_store?.id) {
      setLoader(true);

      _getById(
        endpoints.Product + controller.GetByStore,
        _store.id,
        actions.ActionType.SetProducts,
        () => setLoader(false)
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  //------------------------------------------------
  // Product Table Data
  //------------------------------------------------
  const tableData = useMemo(() => {
    return (_productsList || []).map((obj) => ({
      ...obj,
      imageStatus:
        obj.imgs?.length > 0 && obj.imgs[0]?.imgUrl
          ? t("hasImage")
          : t("noImage"),

      category: obj.category?.name || "",
      subCat: obj.parentCat?.name || "",

      unit:
        UnitType.find((j) => j.id === obj.unit)?.name ?? "",

      tax:
        BoolType.find((j) => j.id === obj.tax)?.name ?? "",

      profitMargin: obj.profitMargin
        ? obj.profitMargin + "%"
        : "",
    }));
  }, [_productsList, UnitType, BoolType, t]);

  //------------------------------------------------
  // View Product
  //------------------------------------------------
  const handleView = (data) => {
    const product = tableData.find(
      (item) => Number(item.id) === Number(data.id)
    );

    setViewData(product || data);
    setShowView(true);
  };

  const handleCloseView = () => {
    setShowView(false);
    setViewData(null);
  };

  //------------------------------------------------
  // Table Columns
  //------------------------------------------------
  const screenColumns = [
    { id: "actions", label: t("actions") },
    { id: "name", label: t("name") },
    { id: "imageStatus", label: t("image") },
    { id: "barcode", label: t("barcode") },
    { id: "category", label: t("category") },
    { id: "subCat", label: t("subcategory") },
    { id: "unit", label: t("unitType") },
    { id: "tax", label: t("tax") },
    { id: "purchPrice", label: t("purchasePrice") },
    { id: "profitMargin", label: t("profitMargin") },
    { id: "sellPrice", label: t("sellPrice") },
    { id: "alertQty", label: t("alertQty") },
  ];

  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Barcode */}
      <Tooltip arrow title={t("printBarcode")}>
        <IconButton
          onClick={() => handleBarcode(data.barcode)}
          size="small"
          className="btn-primary"
        >
          <HorizontalSplitRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Edit */}
      <Tooltip arrow title={t("editThisProduct")}>
        <IconButton
          onClick={() =>
            navigate(`product-form/${data.id}`)
          }
          size="small"
          className="btn-primary"
        >
          <EditRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("deleteThisProduct")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Add Product Button
  //------------------------------------------------
  const HeaderComp = () => (
    <Button
      onClick={() => navigate("product-form")}
      sx={{
        width: { md: "180px", xs: "100%" },
        mb: { xs: "10px", md: "0px" },
        fontSize: "17px",
        padding: "8px",
      }}
    >
      {t("addProduct")}
    </Button>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Heading and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("products", {
            defaultValue: "Products",
          })}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("products", {
            defaultValue: "Products",
          })}
        />
      </Box>

      {/* Normal Products Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Products List */}
      <Box
        ref={reportRef}
        className="products-list-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("products", {
            defaultValue: "Products",
          })}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* Alert */}
      <Alert alert={alert} setAlert={setAlert} />

      {/* Barcode Dialog */}
      <Dialog
        show={showBarcode}
        onClose={false}
        maxWidth="lg"
      >
        <BarcodeGen
          value={barCode}
          setShowPopup={setShowBarcode}
        />
      </Dialog>

      {/* View Product Dialog */}
      {showView && viewData && (
        <Dialog
          show={showView}
          onClose={handleCloseView}
          maxWidth="md"
        >
          <Box sx={{ p: 3 }}>
            {/* Print Preview */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <ReportPrintButton
                reportRef={detailsRef}
                paperSize="Letter"
                reportTitle={t("productDetails", {
                  defaultValue: "Product Details",
                })}
              />
            </Box>

            {/* Screen Details */}
            <Box
              sx={{
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <ProductDetailsContent
                product={viewData}
                store={_store}
                t={t}
              />
            </Box>

            {/* Printable Details */}
            <Box
              ref={detailsRef}
              className="product-details-print"
              sx={{
                position: "absolute",
                left: "-10000px",
                top: 0,
                width: "1000px",
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <ProductDetailsContent
                product={viewData}
                store={_store}
                t={t}
              />
            </Box>

            {/* Close */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
              }}
            >
              <MuiButton
                variant="contained"
                onClick={handleCloseView}
              >
                {t("close")}
              </MuiButton>
            </Box>
          </Box>
        </Dialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  _productsList: state.pos.productsList,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
  _delete: actions.Delete,
  _clear: actions.clear,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(Products);
